import { CompiledChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { CompiledInfluxQueryObject, InfluxQuery, TagSelector } from '@/composables/charts/influx-charts/query-builders/InfluxTypes'
import { WhereClauses } from '@/composables/charts/influx-charts/chart-serialization/WhereClauses'
import { SerializedRegionChart } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import { AbstractInfluxLineChartBuilder } from '@/composables/charts/influx-charts/chart-builders/AbstractInfluxLineChartBuilder'

export class RegionInfluxChartBuilder extends AbstractInfluxLineChartBuilder {
  regionType: string
  regionSpecTagSelector: TagSelector<string>
  labelTagSelector?: TagSelector<'label'>
  cameraTagSelector?: TagSelector<'camera'>

  constructor(serialized: SerializedRegionChart) {
    super(serialized)
    this.regionType = serialized.regionType
    this.regionSpecTagSelector = serialized.regionSpecTagSelector
    this.labelTagSelector = serialized.labelTagSelector
    this.cameraTagSelector = serialized.cameraTagSelector
  }

  serialize(): SerializedRegionChart {
    return {
      ...super.serialize(),
      chartType: CHART_TYPES.REGIONS,
      regionType: this.regionType,
      regionSpecTagSelector: this.regionSpecTagSelector,
      ...(this.labelTagSelector && { labelTagSelector: this.labelTagSelector }),
      ...(this.cameraTagSelector && { cameraTagSelector: this.cameraTagSelector }),
    }
  }

  buildQuery(): CompiledInfluxQueryObject {
    const compiledTimeSetup: CompiledChartTimeSetup = this.timeSetup.compile()
    const groupByTags = []

    if (this.regionSpecTagSelector.groupByValues) groupByTags.push(this.regionType)
    if (this.labelTagSelector && this.labelTagSelector.groupByValues) groupByTags.push('label')
    if (this.cameraTagSelector && this.cameraTagSelector.groupByValues) groupByTags.push('camera')

    if (this.groupByTags) groupByTags.push(...this.groupByTags)
    const whereFilters: WhereClauses = {}
    if (!this.regionSpecTagSelector.useAll && this.regionSpecTagSelector.specificValues && this.regionSpecTagSelector.specificValues.length > 0) {
      whereFilters[this.regionType] = this.regionSpecTagSelector.specificValues
    }
    if (
      this.cameraTagSelector &&
      !this.cameraTagSelector.useAll &&
      this.cameraTagSelector.specificValues &&
      this.cameraTagSelector.specificValues.length > 0
    ) {
      whereFilters['camera'] = this.cameraTagSelector.specificValues
    }
    if (
      this.labelTagSelector &&
      !this.labelTagSelector.useAll &&
      this.labelTagSelector.specificValues &&
      this.labelTagSelector.specificValues.length > 0
    ) {
      whereFilters['label'] = this.labelTagSelector.specificValues
    }
    if (this.whereClauses) {
      Object.assign(whereFilters, this.whereClauses)
    }

    const innerQueryCustomPredicates: string[] = []
    if (this.tagSelectors)
      for (const tagSelector of this.tagSelectors) {
        if (!tagSelector.useAll && tagSelector.specificValues && tagSelector.specificValues.length > 0)
          whereFilters[tagSelector.tag] = tagSelector.specificValues
        if (tagSelector.groupByValues) groupByTags.push(tagSelector.tag)
        if (!tagSelector.allowEmptyValues) innerQueryCustomPredicates.push(`${tagSelector.tag} != ''`)
      }
    if (this.transientTagSelectors)
      for (const tagSelector of this.transientTagSelectors) {
        if (!tagSelector.useAll && tagSelector.specificValues && tagSelector.specificValues.length > 0)
          whereFilters[tagSelector.tag] = tagSelector.specificValues
        if (tagSelector.groupByValues) groupByTags.push(tagSelector.tag)
        if (!tagSelector.allowEmptyValues) innerQueryCustomPredicates.push(`${tagSelector.tag} != ''`)
      }
    const innerQuery: InfluxQuery = {
      columns: { regions: 'sum@inner' },
      time: {
        start: compiledTimeSetup.start,
        end: compiledTimeSetup.end,
      },
      timeResolution: '60s',
      groupByTags: groupByTags.length > 0 ? groupByTags : undefined,
    }
    const outerQuery: InfluxQuery = {
      columns: { inner: 'mean@y', time: true },
      timeResolution: `${compiledTimeSetup.timeResolution}ms`,
      groupByTags: groupByTags.length > 0 ? groupByTags : undefined,
    }
    if (Object.keys(whereFilters).length > 0) innerQuery.where = whereFilters

    if (this.labelTagSelector && !this.labelTagSelector.allowEmptyValues) {
      innerQueryCustomPredicates.push(`label != ''`)
    }
    if (this.cameraTagSelector && !this.cameraTagSelector.allowEmptyValues) {
      innerQueryCustomPredicates.push(`camera != ''`)
    }
    if (!this.regionSpecTagSelector.allowEmptyValues) {
      innerQueryCustomPredicates.push(`${this.regionType} != ''`)
    }
    innerQuery.customPredicates = [...new Set(innerQueryCustomPredicates)]
    return {
      path: '/api/data/regions',
      query: [innerQuery, outerQuery],
    }
  }

  autoInferChartEntity(serialized?: SerializedRegionChart): string | undefined {
    const localRegionType = this.regionType ? this.regionType : serialized?.regionType
    if (!localRegionType) return undefined
    if (localRegionType === 'stalltechnik') return 'frontend-charts.barnEquipment'
    if (localRegionType === 'stallbereich_vh') return 'frontend-charts.frontBack'
    if (localRegionType === 'stallbereich_lr') return 'frontend-charts.leftRight'
    return `frontend-charts.${localRegionType}`
  }
}
